(ns io.daskul.tutorial.webapp.domain.auth.events
  "Authentication events - login, logout, token management.

  Also registers the handler for ::shared.events/unauthorized (401 auto-logout).
  Dependency direction: domain (auth) → infra (shared, config) — no cycles."
  (:require
   [ajax.core]
   [re-frame.core :as rf]
   [io.daskul.webapp.auth.throttle :as throttle]
   [io.daskul.tutorial.webapp.shared.api.client :as api]
   [io.daskul.tutorial.webapp.shared.events :as shared.events]
   [io.daskul.tutorial.webapp.router :as router]
   [io.daskul.tutorial.webapp.domain.bootstrap.events :as bootstrap.events]
   [io.daskul.tutorial.webapp.domain.active-context.events :as ac.events]
   [clojure.string :as str]))

(def token-key "auth-token")

(defn- decode-jwt-payload
  "Decode JWT payload (middle part). Returns nil if invalid."
  [token]
  (try
    (-> token (str/split #"\.") second js/atob js/JSON.parse
        (js->clj :keywordize-keys true))
    (catch :default _ nil)))

;; ============================================================================
;; 401 Unauthorized → auto-logout
;; ============================================================================

;; Handler for ::shared.events/unauthorized, dispatched by shared/events on 401.
;; Registered here so shared/events stays free of domain dependencies.
(rf/reg-event-fx
 ::shared.events/unauthorized
 (fn [_ _]
   {:dispatch [::logout]}))

;; ============================================================================
;; Initialize Auth
;; ============================================================================

(rf/reg-event-fx
 ::initialize
 (fn [{:keys [db]} _]
   (let [token (.getItem js/localStorage token-key)
         user (when token (decode-jwt-payload token))]
     (cond-> {:db (assoc db :auth {:token token
                                   :loading? false})}
       ;; Boot the app (config + flags + active context) if already authenticated
       token (assoc :dispatch [::bootstrap.events/bootstrap
                               {:school-id (:school-id user)
                                :branch-id (:branch-id user)
                                :available-contexts (:available-contexts user)}])
       ;; Set Sentry user context for existing session
       user (assoc :sentry/set-user user)
       ;; Set Clarity user context for existing session
       user (assoc :clarity/identify user)))))

;; ============================================================================
;; Login
;; ============================================================================

(rf/reg-event-fx
 ::login
 (fn [{:keys [db]} [_ credentials]]
   {:db (assoc-in db [:auth :loading?] true)
    :http-xhrio {:method :post
                 :uri (str api/api-url "/auth/login")
                 :params credentials
                 :format (ajax.core/json-request-format)
                 :response-format (ajax.core/json-response-format {:keywords? true})
                 :on-success [::login-success]
                 :on-failure [::login-failure]}}))

(rf/reg-event-fx
 ::login-success
 (fn [{:keys [db]} [_ response]]
   (let [token (:token response)
         user (decode-jwt-payload token)]
     (cond-> {:db (-> db
                      (assoc-in [:auth :token] token)
                      (assoc-in [:auth :loading?] false)
                      (assoc-in [:auth :error] nil)
                      (assoc-in [:auth :retry-after] nil))
              :localstorage/set [token-key token]
              :dispatch-n [[::bootstrap.events/bootstrap
                            {:school-id (:school-id user)
                             :branch-id (:branch-id user)
                             :available-contexts (:available-contexts user)}]
                           [::router/navigate :home]]}
       user (assoc :sentry/set-user user)
       user (assoc :clarity/identify user)))))

;; A 429 from the login endpoint (SMS-280) is a rate-limit lockout, not a bad
;; password. Surface it distinctly, with a live countdown driven off the
;; server's :retry-after. Logic lives in io.daskul.webapp.auth.throttle (pure,
;; node-tested); these handlers just wire :start-tick?/:continue? to a timer.
(rf/reg-event-fx
 ::login-failure
 (fn [{:keys [db]} [_ failure]]
   (let [{:keys [db start-tick?]} (throttle/apply-login-failure db failure)]
     (cond-> {:db db}
       start-tick? (assoc :dispatch-later
                          [{:ms throttle/tick-ms :dispatch [::throttle-tick]}])))))

(rf/reg-event-fx
 ::throttle-tick
 (fn [{:keys [db]} _]
   (let [{:keys [db continue?]} (throttle/apply-throttle-tick db)]
     (cond-> {:db db}
       continue? (assoc :dispatch-later
                        [{:ms throttle/tick-ms :dispatch [::throttle-tick]}])))))

;; ============================================================================
;; Logout
;; ============================================================================

(rf/reg-event-fx
 ::logout
 (fn [{:keys [db]} _]
   {:db (-> db
            (assoc-in [:auth :token] nil)
            (assoc-in [:auth :error] nil)
            (dissoc :config)
            (dissoc :feature-flags)
            ;; Reset the boot gate so the next session re-gates from scratch.
            (assoc-in [:bootstrap :started?] false))
    :localstorage/remove token-key
    :sentry/clear-user true
    :clarity/clear true
    :dispatch-n [[::ac.events/clear]
                 [::router/navigate :login]]}))
